import React, { useCallback, useMemo } from 'react';
import { Form } from 'react-bootstrap';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { MemoryDTO } from 'components/pages/Book/MemoryBank/memoryBankTypes';
import { DragHandle } from 'components/ui/DragHandle/DragHandle';
import { HubProgressBar } from 'dorian-shared/types/branch/HubOption';
import {
  HUB_PROGRESS_BAR_LABEL_MAX_LENGTH,
  HUB_PROGRESS_BAR_LABEL_WARN_LENGTH,
} from '../hubStepDefaults';
import { HubValueSelect } from '../HubOptions/HubValueSelect';
import './ProgressBars.scss';

interface ProgressBarRowProps {
  bar: HubProgressBar;
  variables: MemoryDTO[];
  // Live/published episode: backend's updateHubProgressBarsLive only persists label + sortOrder.
  // Structural changes (variable refs, add, delete) must be blocked to prevent silent data loss.
  isStructuralLocked: boolean;
  onChange: (uuid: string, changes: Partial<HubProgressBar>) => void;
  onRemove: (uuid: string) => void;
}

// varValue === 0 means "no selection" per existing SubConditionRow contract.
type SideKey = 'numerator' | 'denominator';
type ValueSelectChange = { intValue?: string; varValue?: number };

function sideChangesFromValueSelect(
  data: ValueSelectChange,
  side: SideKey,
): Partial<HubProgressBar> {
  const typeKey = `${side}Type` as const;
  const literalKey = `${side}Literal` as const;
  const varKey = `${side}VariableId` as const;

  if (data.varValue !== undefined) {
    const variableId = data.varValue === 0 ? null : data.varValue;
    return {
      [typeKey]: 'variable',
      [literalKey]: null,
      [varKey]: variableId,
    } as Partial<HubProgressBar>;
  }

  if (data.intValue !== undefined) {
    return {
      [typeKey]: 'literal',
      [literalKey]: data.intValue,
      [varKey]: null,
    } as Partial<HubProgressBar>;
  }

  return {};
}

export function ProgressBarRow({
  bar,
  variables,
  isStructuralLocked,
  onChange,
  onRemove,
}: ProgressBarRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: bar.uuid,
    // DnD stays enabled — sortOrder IS persisted by updateHubProgressBarsLive.
    transition: {
      duration: 250,
      easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
    },
  });

  const style = useMemo(
    () => ({
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    }),
    [transform, transition, isDragging],
  );

  const handleLabelChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(bar.uuid, { label: e.target.value });
    },
    [bar.uuid, onChange],
  );

  const handleNumeratorChange = useCallback(
    (data: { intValue?: string; varValue?: number }) => {
      onChange(bar.uuid, sideChangesFromValueSelect(data, 'numerator'));
    },
    [bar.uuid, onChange],
  );

  const handleDenominatorChange = useCallback(
    (data: { intValue?: string; varValue?: number }) => {
      onChange(bar.uuid, sideChangesFromValueSelect(data, 'denominator'));
    },
    [bar.uuid, onChange],
  );

  const handleRemove = useCallback(() => onRemove(bar.uuid), [bar.uuid, onRemove]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="progress-bar-row"
      data-testid="progress-bar-row"
      data-bar-uuid={bar.uuid}
    >
      <DragHandle
        attributes={attributes}
        listeners={listeners}
        className="align-self-center"
      />

      <Form.Group className="progress-bar-row__label mb-0 hub-settings-field">
        <Form.Label className="small mb-0">Label</Form.Label>
        <Form.Control
          size="sm"
          value={bar.label}
          onChange={handleLabelChange}
          placeholder="e.g. Dates"
          maxLength={HUB_PROGRESS_BAR_LABEL_MAX_LENGTH}
          className={(bar.label?.length ?? 0) > HUB_PROGRESS_BAR_LABEL_WARN_LENGTH ? 'text-warn' : ''}
        />
        <Form.Text className="char-limit-info-box" style={{ top: 14, bottom: 'auto' }}>
          {HUB_PROGRESS_BAR_LABEL_WARN_LENGTH - (bar.label?.length ?? 0)}
        </Form.Text>
      </Form.Group>

      <Form.Group className="progress-bar-row__side mb-0">
        <Form.Label className="small mb-0">Numerator</Form.Label>
        <HubValueSelect
          subConditionUuid={bar.numeratorUuid}
          intValue={bar.numeratorType === 'literal' ? bar.numeratorLiteral ?? undefined : undefined}
          varValue={bar.numeratorType === 'variable' ? bar.numeratorVariableId ?? undefined : undefined}
          variables={variables}
          excludeVariableId={
            bar.denominatorType === 'variable' ? bar.denominatorVariableId ?? undefined : undefined
          }
          onChange={handleNumeratorChange}
          disabled={isStructuralLocked}
          placeholder="Value or variable"
        />
      </Form.Group>

      <Form.Group className="progress-bar-row__side mb-0">
        <Form.Label className="small mb-0">Denominator</Form.Label>
        <HubValueSelect
          subConditionUuid={bar.denominatorUuid}
          intValue={bar.denominatorType === 'literal' ? bar.denominatorLiteral ?? undefined : undefined}
          varValue={bar.denominatorType === 'variable' ? bar.denominatorVariableId ?? undefined : undefined}
          variables={variables}
          excludeVariableId={
            bar.numeratorType === 'variable' ? bar.numeratorVariableId ?? undefined : undefined
          }
          onChange={handleDenominatorChange}
          disabled={isStructuralLocked}
          placeholder="Value or variable"
        />
      </Form.Group>

      <button
        type="button"
        className="btn btn-outline-danger btn-sm progress-bar-row__remove"
        onClick={handleRemove}
        disabled={isStructuralLocked}
        title={isStructuralLocked ? 'Cannot remove in live episode' : 'Remove progress bar'}
      >
        ×
      </button>
    </div>
  );
}
